import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

/**
 * Tutoriel d'onboarding pour les nouveaux utilisateurs
 * Affiche des bulles guidées qui pointent vers les éléments importants
 * Le tutoriel n'apparaît qu'UNE SEULE FOIS après la création du compte
 */

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Bienvenue sur SmartBouffe!',
    description: 'Laissez-nous vous guider pour découvrir toutes les fonctionnalités de votre livre de recettes virtuel.',
    target: null,
    position: 'center'
  },
  {
    id: 'how-to-link',
    title: 'Comment importer une recette?',
    description: 'Ouvrez TikTok, Instagram, YouTube ou Facebook, trouvez une vidéo de recette, appuyez sur "Partager" puis "Copier le lien". Revenez ici et collez-le!',
    target: null,
    position: 'center',
    illustration: 'copy-link'
  },
  {
    id: 'share-shortcut',
    title: 'Encore plus rapide sur Android!',
    description: 'Appuyez sur "Partager" dans votre app, puis choisissez SmartBouffe directement dans la liste. Le lien sera importé automatiquement!',
    target: null,
    position: 'center',
    illustration: 'share-android'
  },
  {
    id: 'add-recipe',
    title: 'Ajouter une recette',
    description: 'Cliquez sur ce bouton "+" pour ajouter une nouvelle recette. Collez un lien vidéo, importez une photo, ou créez manuellement.',
    target: '[data-testid="floating-add-btn"]',
    position: 'top',
    highlight: true
  },
  {
    id: 'categories',
    title: 'Vos catégories',
    description: 'Organisez vos recettes par catégories : Souper, Dessert, Cocktails... Vous pouvez en créer autant que vous voulez!',
    target: '[data-testid="filter-btn"]',
    position: 'bottom',
    highlight: true
  },
  {
    id: 'search',
    title: 'Rechercher une recette',
    description: 'Trouvez rapidement une recette par nom ou par ingrédient.',
    target: '[data-testid="search-input"]',
    position: 'bottom',
    highlight: true
  },
  {
    id: 'profile',
    title: 'Votre profil',
    description: 'Configurez vos préférences alimentaires et allergies. SmartBouffe adaptera les recettes pour vous!',
    target: '[data-testid="profile-link"]',
    position: 'bottom-left',
    highlight: true
  },
  {
    id: 'done',
    title: 'Bonne cuisine!',
    description: 'Vous êtes prêt! Commencez par ajouter votre première recette vidéo.',
    target: null,
    position: 'center'
  }
];

export default function OnboardingTutorial({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  const step = TUTORIAL_STEPS[currentStep];

  useEffect(() => {
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        
        // Scroll vers l'élément si nécessaire
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep, step.target]);

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    handleComplete();
  };

  if (!isVisible) return null;

  // Position de la bulle
  const getBubblePosition = () => {
    if (!targetRect || step.position === 'center') {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const margin = 20;
    const bubbleHeight = 200;
    const bubbleWidth = 320;

    switch (step.position) {
      case 'top':
        return {
          bottom: `${window.innerHeight - targetRect.top + margin}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'bottom':
        return {
          top: `${targetRect.bottom + margin}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'bottom-left':
        return {
          top: `${targetRect.bottom + margin}px`,
          right: `${window.innerWidth - targetRect.right}px`
        };
      case 'left':
        return {
          top: `${targetRect.top + targetRect.height / 2}px`,
          right: `${window.innerWidth - targetRect.left + margin}px`,
          transform: 'translateY(-50%)'
        };
      case 'right':
        return {
          top: `${targetRect.top + targetRect.height / 2}px`,
          left: `${targetRect.right + margin}px`,
          transform: 'translateY(-50%)'
        };
      default:
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        };
    }
  };

  // Flèche pointant vers la cible
  const getArrowStyle = () => {
    if (!targetRect || step.position === 'center') return null;

    const arrowSize = 12;
    
    switch (step.position) {
      case 'top':
        return {
          position: 'absolute',
          bottom: `-${arrowSize}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderTop: `${arrowSize}px solid #87A96B`
        };
      case 'bottom':
      case 'bottom-left':
        return {
          position: 'absolute',
          top: `-${arrowSize}px`,
          left: step.position === 'bottom-left' ? 'auto' : '50%',
          right: step.position === 'bottom-left' ? '30px' : 'auto',
          transform: step.position === 'bottom-left' ? 'none' : 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid #87A96B`
        };
      default:
        return null;
    }
  };

  return (
    <>
      {/* Overlay sombre */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9999998
        }}
        onClick={handleSkip}
      />

      {/* Zone de highlight autour de l'élément cible */}
      {targetRect && step.highlight && (
        <div
          style={{
            position: 'fixed',
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            borderRadius: '12px',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
            zIndex: 9999998,
            pointerEvents: 'none',
            border: '3px solid #87A96B',
            animation: 'pulse-highlight 1.5s infinite'
          }}
        />
      )}

      {/* Bulle de tutoriel */}
      <div
        style={{
          position: 'fixed',
          ...getBubblePosition(),
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          maxWidth: '320px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          zIndex: 9999999,
          border: '3px solid #87A96B'
        }}
      >
        {/* Flèche */}
        {getArrowStyle() && <div style={getArrowStyle()} />}

        {/* Bouton fermer */}
        <button
          onClick={handleSkip}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#8B7355',
            padding: '4px'
          }}
          aria-label="Fermer le tutoriel"
        >
          <X size={20} />
        </button>

        {/* Contenu */}
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#3A4A3E',
            marginBottom: '0.5rem',
            paddingRight: '24px'
          }}>
            {step.title}
          </h3>

          {/* Illustration pour l'étape "copier le lien" */}
          {step.illustration === 'copy-link' && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
              margin: '0.75rem 0',
              flexWrap: 'wrap'
            }}>
              {[
                { name: 'TikTok', color: '#000000', icon: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' },
                { name: 'Instagram', color: '#E4405F', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' },
                { name: 'YouTube', color: '#FF0000', icon: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
                { name: 'Facebook', color: '#1877F2', icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' }
              ].map(social => (
                <div key={social.name} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '10px',
                    background: social.color, display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d={social.icon}/></svg>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#666', marginTop: 2 }}>{social.name}</div>
                </div>
              ))}
            </div>
          )}

          {/* Illustration pour le partage Android */}
          {step.illustration === 'share-android' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              margin: '0.75rem 0',
              padding: '0.75rem',
              background: '#f0f7ec',
              borderRadius: '12px'
            }}>
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#87A96B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                <span style={{ fontSize: '0.65rem', color: '#5D6F61', fontWeight: 600 }}>Partager</span>
              </div>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#87A96B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', background: '#87A96B', borderRadius: '8px'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/></svg>
                <span style={{ fontSize: '0.75rem', color: 'white', fontWeight: 700 }}>SmartBouffe</span>
              </div>
            </div>
          )}

          <p style={{
            fontSize: '0.9rem',
            color: '#5D6F61',
            lineHeight: '1.5'
          }}>
            {step.description}
          </p>
        </div>

        {/* Indicateurs de progression */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          marginBottom: '1rem'
        }}>
          {TUTORIAL_STEPS.map((s, index) => (
            <div
              key={`step-dot-${s.id}`}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: index === currentStep ? '#87A96B' : '#D4C5B0',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>

        {/* Boutons de navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '0.5rem 1rem',
              border: '1px solid #D4C5B0',
              borderRadius: '8px',
              background: 'transparent',
              color: currentStep === 0 ? '#ccc' : '#5D6F61',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: '500'
            }}
          >
            <ArrowLeft size={16} />
            Précédent
          </button>

          <button
            onClick={handleSkip}
            style={{
              padding: '0.5rem',
              background: 'none',
              border: 'none',
              color: '#8B7355',
              cursor: 'pointer',
              fontSize: '0.8rem',
              textDecoration: 'underline'
            }}
          >
            Passer
          </button>

          <button
            onClick={handleNext}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '8px',
              background: '#87A96B',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600'
            }}
          >
            {currentStep === TUTORIAL_STEPS.length - 1 ? (
              <>
                <CheckCircle size={16} />
                Terminer
              </>
            ) : (
              <>
                Suivant
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse-highlight {
          0%, 100% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 0 0 rgba(135, 169, 107, 0.4);
          }
          50% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 0 12px rgba(135, 169, 107, 0);
          }
        }
      `}</style>
    </>
  );
}

/**
 * Hook pour vérifier si le tutoriel doit être affiché
 * Utilise la base de données pour stocker l'état (lié au compte, pas au navigateur)
 */
export function useTutorial(profile) {
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    // Vérifier si le profil existe et si l'utilisateur a déjà vu le tutoriel
    if (profile && profile.has_seen_tutorial === false) {
      // Petit délai pour laisser le temps à la page de se charger
      const timer = setTimeout(() => setShowTutorial(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [profile]);

  const completeTutorial = async () => {
    setShowTutorial(false);
    
    // Marquer le tutoriel comme complété dans la base de données
    try {
      await axios.post(`${API}/user/tutorial-completed`, {}, { withCredentials: true });
    } catch (error) {
      // Non bloquant - le tutoriel est fermé localement même si l'API échoue
    }
  };

  return { showTutorial, completeTutorial };
}
